import React, { useEffect, useState } from 'react';

import './Tabs.scss';
import { Svg } from 'src/components/Svg/Svg';
import { SvgIconNames } from 'src/components/Svg/icons-types';

import { useBreakpointMatch } from '../../hooks/useBreakpoint';

export type TabItem = {
    content: React.ReactNode;
    icon?: string;
    id: string;
    title: string;
};

export type TabsProps = {
    tabs: TabItem[];
};

export function Tabs(props: TabsProps) {
    /**
     * Accessibility attributes:
     * role: This attribute indicates the role of an element
     * aria-controls: This attribute associates a tab with its corresponding tab panel.
     * aria-selected: This attribute indicates whether a tab is currently selected.
     * aria-labelledby: This attribute associates a tab panel with its corresponding tab.
     * tabIndex: This attribute makes an element focusable and includes it in the keyboard navigation order.
     */

    const { isMob } = useBreakpointMatch();
    const [openIndexes, toggleTab] = useState<number[]>([]);

    useEffect(() => {
        if (!isMob) {
            toggleTab([0]);
        }
    }, [isMob]);

    return (
        <div className="tabs" role="tablist">
            {props.tabs.map((item, index) => {
                const active = openIndexes.includes(index);

                return (
                    <React.Fragment key={`${item.title}--${item.id}`}>
                        <span
                            className="tabs__item"
                            data-ui-active={active}
                            role="tab"
                            aria-controls={`${item.title}-panel`}
                            aria-selected={active}
                            tabIndex={active ? 0 : -1}
                        >
                            <a
                                className="tabs__link"
                                data-ui-active={active}
                                href={`#${item.id}`}
                                onClick={(event) => {
                                    event.preventDefault();

                                    toggleTab((tabs) => {
                                        if (isMob) {
                                            if (tabs.indexOf(index) > -1) {
                                                return tabs.filter((x) => x !== index);
                                            }

                                            return tabs.concat(index);
                                        }

                                        return [index];
                                    });
                                }}
                            >
                                <span className="tabs__title">
                                    {item.icon && <Svg name={item.icon as SvgIconNames} />} {item.title}
                                </span>
                                <Svg name="arrow-down" />
                            </a>
                        </span>
                        <div
                            className="tabs__body"
                            data-ui-active={active}
                            role="tabpanel"
                            aria-labelledby={`${item.title}-tab`}
                            id={`${item.title}-panel`}
                        >
                            {item.content}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}
